import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    CategoryDTO,
    useDeleteCategoryApi,
    useSaveCategoryApi,
    useSearchCategoryApi,
} from '../../../api/category-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveCategoryModal } from './save-category-modal';

export function CategoryApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryDTO | undefined>();
    const categoryInit = useSearchCategoryApi();
    const saveCategory = useSaveCategoryApi();
    const deleteCategory = useDeleteCategoryApi();

    const { result, error, busy, initialized, initializing } = categoryInit;

    const columns: IBasicTableColumn<CategoryDTO>[] = useMemo(
        () => [
            {
                Header: <T>code</T>,
                name: 'code',
                width: 200,
            },
            {
                Header: <T>title</T>,
                name: 'title',
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        categoryInit.post(null, {});
    }, [initialized, initializing, error, categoryInit]);

    const handleDelete = (category: CategoryDTO) => {
        bd.msgbox(
            translate('delete-category'),
            translate('confirm-delete-category'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteCategory.remove(
                            category,
                            () => {
                                categoryInit.updateResult({
                                    ...result,
                                    categories: result.categories.filter(
                                        b => b.code !== category.code
                                    ),
                                });
                                notify.dark(translate('selected-category-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedCategory(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (category: CategoryDTO) => {
        saveCategory.post(
            null,
            category,
            res => {
                if (selectedCategory) {
                    categoryInit.updateResult({
                        ...result,
                        categories: result.categories.map(b =>
                            b.code === category.code ? res.category : b
                        ),
                    });
                } else {
                    categoryInit.updateResult({
                        ...result,
                        categories: [res.category, ...result.categories],
                    });
                }

                setShowModal(false);
                setSelectedCategory(undefined);
                notify.success(
                    selectedCategory
                        ? translate('selected-category-updated-successfully')
                        : translate('new-category-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => categoryInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="category"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-category</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedCategory(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.categories}
                        onRowClick={({ row }) => {
                            setSelectedCategory(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveCategoryModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedCategory(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedCategory={selectedCategory}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
